import React, { useEffect, useMemo, useState } from "react";
import { makeAPI } from "../../services/api";
import PhoneVerificationModal from "../common/phone_verification_modal";

const INITIAL_FORM = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "",
    dob: "",
    country: "",
    address1: "",
    address2: "",
    state: "",
    postal: "",
};

function normalizeDate(value) {
    if (!value) return "";
    return value.includes("T") ? value.split("T")[0] : value;
}

export default function AccountSettings() {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [userInfoData, setUserInfoData] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [showPhoneVerification, setShowPhoneVerification] = useState(false);
    const todayDate = new Date().toISOString().split("T")[0];

    function updateStoredUser(partialUser) {
        try {
            const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
            const updatedUser = { ...currentUser, ...partialUser };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(new CustomEvent("auth:updated"));
            return updatedUser;
        } catch (error) {
            console.error("Failed to sync updated user details:", error);
            return null;
        }
    }

    async function loadAccountSetting() {
        try {
            const getUserData = await makeAPI("/api/get_user_info", "post", false, {
                user_id: localUser?.user_id,
            });

            if (getUserData?.status === "S" && getUserData?.data) {
                const data = getUserData.data;
                const mappedData = {
                    firstName: data.name || "",
                    lastName: data.last_name || "",
                    email: data.email || "",
                    phone: data.phone_no || "",
                    gender: data.gender || "",
                    dob: normalizeDate(data.dob),
                    country: data.country || "",
                    address1: data.address1 || "",
                    address2: data.address2 || "",
                    state: data.state || "",
                    postal: data.postal_code || "",
                };

                setFormData(mappedData);
                setUserInfoData(mappedData);
                setPhoneVerified(data.phone_verified === true);
                setApiError("");
            } else {
                setApiError(getUserData?.msg || "Unable to fetch account settings.");
            }
        } catch (error) {
            setApiError("Failed to load account settings. Please try again.");
        }
    }

    useEffect(() => {
        loadAccountSetting();
    }, []);

    useEffect(() => {
        if (apiSuccess || apiError) {
            const timer = setTimeout(() => {
                setApiSuccess("");
                setApiError("");
            }, 5000);

            return () => clearTimeout(timer); // cleanup
        }
    }, [apiSuccess, apiError])

    function validateField(name, value, fullData = formData) {
        const trimmed = typeof value === "string" ? value.trim() : value;

        switch (name) {
            case "firstName":
                if (!trimmed) return "First name is required.";
                if (trimmed.length < 3) return "First name must be at least 3 characters.";
                if (trimmed.length > 30) return "First name must be at most 30 characters.";
                // if (!/^[A-Za-z\s]+$/.test(trimmed)) return "First name should contain only letters.";
                return "";
            case "lastName":
                if (!trimmed) return "";
                if (trimmed.length > 20) return "Last name must be at most 20 characters.";
                // if (!/^[A-Za-z\s]+$/.test(trimmed)) return "Last name should contain only letters.";
                return "";
            case "email":
                if (!trimmed) return "Email is required.";
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return "Please enter a valid email.";
                return "";
            // case "phone":
            //     if (!trimmed) return "Phone number must be 10 digits.";
            //     if (!/^\d{10}$/.test(trimmed)) return "Phone number must be 10 digits.";
            //     return "";
            case "gender":
                if (!trimmed) return "";
                if (!["male", "female"].includes(trimmed.toLowerCase())) return "Please select a valid gender.";
                return "";
            case "dob":
                if (!trimmed) return "";
                if (Number.isNaN(Date.parse(trimmed))) return "Please enter a valid date of birth.";
                if (new Date(trimmed) > new Date()) return "Date of birth cannot be in the future.";
                return "";
            case "country":
                if (!trimmed) return "";
                if (trimmed.length > 56) return "Country name is too long.";
                return "";
            case "address1":
                if (!trimmed) return "";
                if (trimmed.length > 350) return "Address line 1 is too long.";
                return "";
            case "address2":
                if (!trimmed) return "";
                if (trimmed.length > 350) return "Address line 2 is too long.";
                return "";
            case "state":
                if ((fullData.address1?.trim() || fullData.address2?.trim()) && !trimmed) {
                    return "State is required when address is provided.";
                }
                if (!trimmed) return "";
                if (trimmed.length > 20) return "State must be at most 20 characters.";
                return "";
            case "postal":
                if ((fullData.address1?.trim() || fullData.address2?.trim() || fullData.state?.trim()) && !trimmed) {
                    return "Postal code is required when address or state is provided.";
                }
                if (!trimmed) return "";
                if (!/^\d{4,6}$/.test(trimmed)) return "Postal code must be 4 to 6 digits.";
                return "";
            default:
                return "";
        }
    }

    function validateForm(data = formData) {
        const nextErrors = {};
        Object.keys(data).forEach((field) => {
            const message = validateField(field, data[field], data);
            if (message) nextErrors[field] = message;
        });
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }

    function handleFieldChange(field, value) {
        const nextValue = field === "phone" || field === "postal" ? value.replace(/\D/g, "") : value;
        setFormData((prev) => {
            const next = { ...prev, [field]: nextValue };
            const message = validateField(field, nextValue, next);
            setErrors((prevErrors) => {
                const updatedErrors = { ...prevErrors, [field]: message || undefined };
                if (["address1", "address2", "state", "postal"].includes(field)) {
                    const stateError = validateField("state", next.state, next);
                    const postalError = validateField("postal", next.postal, next);
                    updatedErrors.state = stateError || undefined;
                    updatedErrors.postal = postalError || undefined;
                }
                return updatedErrors;
            });
            return next;
        });
    }

    const hasFormChanges = useMemo(
        () => JSON.stringify(formData) !== JSON.stringify(userInfoData),
        [formData, userInfoData]
    );

    function handleToggleEdit() {
        setApiError("");
        setApiSuccess("");
        if (isEditing) {
            setFormData(userInfoData);
            setErrors({});
            setIsEditing(false);
            return;
        }
        setIsEditing(true);
    }

    async function handleUserInfoSubmit() {
        setApiError("");
        setApiSuccess("");

        if (!validateForm()) return;
        if (!hasFormChanges) {
            setApiError("No changes to save.");
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                key: localUser?.user_id,
                user_id: localUser?.user_id,
                form_data: {
                    name: formData.firstName.trim(),
                    last_name: formData.lastName.trim(),
                    gender: formData.gender,
                    dob: formData.dob,
                    phone_no: formData.phone,
                    add1: formData.address1.trim(),
                    add2: formData.address2.trim(),
                    country: formData.country.trim(),
                    state: formData.state.trim(),
                    city: "",
                    postal_code: formData.postal,
                },
            };

            const response = await makeAPI("/api/user_info_form", "post", false, payload);
            if (response?.status === "S") {
                setUserInfoData(formData);
                setIsEditing(false);
                setErrors({});
                updateStoredUser({
                    name: formData.firstName.trim(),
                    // last_name: formData.lastName.trim(),
                    // phone_no: formData.phone,
                    // gender: formData.gender,
                    // dob: formData.dob,
                    // country: formData.country.trim(),
                    // state: formData.state.trim(),
                    // postal_code: formData.postal,
                });
                setApiSuccess(response?.msg || "Profile updated successfully.");
            } else {
                setApiError(response?.msg || "Unable to save account settings.");
            }
        } catch (error) {
            setApiError("Something went wrong while saving. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    const inputStyle = {
        border: isEditing ? "1px solid #2d3034" : "none",
        padding: !isEditing ? "0" : "",
        color: !isEditing ? "#808080" : "white",
        backgroundColor: !isEditing ?  "#161616"  : "#000"
    };

    return (
        <>
            <style>{`
                .account-settings-wrap .form-login {
                    margin: 0 auto;
                    max-width: 980px;
                    width: 100%;
                }
                .account-settings-wrap .form-login__box {
                    width: 100%;
                }
                body.dark-theme .account-settings-wrap .uk-input,
                body.dark-theme .account-settings-wrap .uk-select {
                    color: #e4e4e4;
                    background: #161616;
                    border: 1px solid #3d3d3d;
                }
                body.dark-theme .account-settings-wrap .uk-input::placeholder {
                    color: #9ea0a6;
                }
                body.dark-theme .form-login .uk-input {
                    background: #161616 ;
                }
                .account-settings-wrap .uk-alert-danger {
                    background: #161616;
                    padding: 8px;
                    color: #ff4a4a;
                    border-radius: 12px;
                    text-align: center;
                }
                .account-settings-wrap .uk-alert-success {
                    background: #161616;
                    padding: 8px;
                    color: #43d17b;
                    border-radius: 12px;
                    text-align: center;
                }
                .account-settings-wrap .dob-input::-webkit-calendar-picker-indicator {
                    opacity: 0;
                    pointer-events: none;
                }
                .account-settings-wrap .dob-input.dob-editing::-webkit-calendar-picker-indicator {
                    opacity: 1;
                    pointer-events: auto;
                    filter: invert(62%) sepia(73%) saturate(1892%) hue-rotate(358deg) brightness(102%) contrast(104%);
                    cursor: pointer;
                }
            `}</style>

            <div className="account-settings-wrap uk-padding-small uk-padding-medium@m">
                <div className="uk-flex uk-flex-center">
                    <div className="form-login">
                        <div className="form-login__box">
                            <div className="d-flex align-items-center justify-content-end mb-3">
                                {/* <h5 className="text-light mb-0 uk-nav-header">Account Settings</h5> */}
                                <button
                                    type="button"
                                    className="btn btn-sm" 
                                    onClick={handleToggleEdit}
                                    style={{ color: "#ff810e", border: "1px solid #ff810e" }}
                                    title={isEditing ? "Cancel editing" : "Enable editing"}
                                >
                                    <i className={isEditing ? "fa fa-times" : "fa fa-pencil"}></i>
                                </button>
                            </div>

                            {apiError ? <div className="uk-alert-danger uk-margin-small">{apiError}</div> : null}
                            {apiSuccess ? <div className="uk-alert-success uk-margin-small">{apiSuccess}</div> : null}

                            <div className="row g-3">
                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">First Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`uk-input ${errors.firstName ? "is-invalid" : ""}`}
                                placeholder="Enter first name"
                                value={formData.firstName}
                                disabled={!isEditing}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("firstName", e.target.value)}
                            />
                            {errors.firstName ? <div className="invalid-feedback">{errors.firstName}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Last Name</label>
                            <input
                                type="text"
                                className={`uk-input ${errors.lastName ? "is-invalid" : ""}`}
                                placeholder="Enter last name"
                                value={formData.lastName}
                                disabled={!isEditing}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("lastName", e.target.value)}
                            />
                            {errors.lastName ? <div className="invalid-feedback">{errors.lastName}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Email <span className="text-danger">*</span></label>
                            <input
                                type="email"
                                className={`uk-input ${errors.email ? "is-invalid" : ""}`}
                                placeholder="Enter your email"
                                value={formData.email}
                                disabled
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("email", e.target.value)}
                            />
                            {errors.email ? <div className="invalid-feedback">{errors.email}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Phone Number</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                className={`uk-input ${errors.phone ? "is-invalid" : ""}`}
                                placeholder="Phone number"
                                value={formData.phone}
                                disabled={!isEditing || phoneVerified}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("phone", e.target.value)}
                            />
                            {errors.phone ? <div className="invalid-feedback">{errors.phone}</div> : null}
                            {/* {!phoneVerified && isEditing ? (
                                <button
                                    type="button"
                                    className="uk-button uk-button-default uk-margin-small-top"
                                    onClick={() => setShowPhoneVerification(true)}
                                    style={{ color: "#fff", border: "1px solid #ff810e" }}
                                >
                                    Verify Phone
                                </button>
                            ) : null} */}
                            {/* {phoneVerified ? (
                                <small style={{ color: "#7fd98c" }}>Phone verified. Number cannot be edited.</small>
                            ) : null} */}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Gender</label>
                            <select
                                className={`uk-select ${errors.gender ? "is-invalid" : ""}`}
                                value={formData.gender}
                                disabled={!isEditing}
                                onChange={(e) => handleFieldChange("gender", e.target.value)}
                                style={{ ...inputStyle, backgroundColor: "rgb(22, 22, 22)" }}
                            >
                                <option value="">Select your gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                            {errors.gender ? <div className="invalid-feedback">{errors.gender}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Date of Birth</label>
                            <input
                                type="date"
                                className={`uk-input dob-input ${isEditing ? "dob-editing" : ""} ${errors.dob ? "is-invalid" : ""}`}
                                value={formData.dob}
                                disabled={!isEditing}
                                max={todayDate}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("dob", e.target.value)}
                            />
                            {errors.dob ? <div className="invalid-feedback">{errors.dob}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Country</label>
                            <input
                                type="text"
                                className={`uk-input ${errors.country ? "is-invalid" : ""}`}
                                placeholder="Country"
                                value={formData.country}
                                disabled={!isEditing}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("country", e.target.value)}
                            />
                            {errors.country ? <div className="invalid-feedback">{errors.country}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Address Line 1</label>
                            <input
                                type="text"
                                className={`uk-input ${errors.address1 ? "is-invalid" : ""}`}
                                placeholder="House / Street"
                                maxLength={350}
                                value={formData.address1}
                                disabled={!isEditing}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("address1", e.target.value)}
                            />
                            {errors.address1 ? <div className="invalid-feedback">{errors.address1}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Address Line 2</label>
                            <input
                                type="text"
                                className={`uk-input ${errors.address2 ? "is-invalid" : ""}`}
                                placeholder="Area / City"
                                maxLength={350}
                                value={formData.address2}
                                disabled={!isEditing}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("address2", e.target.value)}
                            />
                            {errors.address2 ? <div className="invalid-feedback">{errors.address2}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">State</label>
                            <input
                                type="text"
                                className={`uk-input ${errors.state ? "is-invalid" : ""}`}
                                placeholder="State name"
                                maxLength={20}
                                value={formData.state}
                                disabled={!isEditing}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("state", e.target.value)}
                            />
                            {errors.state ? <div className="invalid-feedback">{errors.state}</div> : null}
                        </div>

                        <div className="col-12 col-md-6 form-group">
                            <label className="form-label text-light">Postal Code</label>
                            <input
                                type="text"
                                className={`uk-input ${errors.postal ? "is-invalid" : ""}`}
                                placeholder="Zip"
                                maxLength={6}
                                value={formData.postal}
                                disabled={!isEditing}
                                style={inputStyle}
                                onChange={(e) => handleFieldChange("postal", e.target.value)}
                            />
                            {errors.postal ? <div className="invalid-feedback">{errors.postal}</div> : null}
                        </div>

                        <div className="col-12 col-lg-6 offset-lg-3 d-flex justify-content-end pt-2">
                            <button
                                id="submit-user-info-btn"
                                type="button"
                                onClick={()=>{handleUserInfoSubmit();}}
                                className="uk-button uk-button-danger"
                                style={{
                                    color: "#fff",
                                    height: "45px",
                                    lineHeight: "11px",
                                    display: isEditing ? "block" : "none",
                                    cursor: hasFormChanges ? "pointer" : "not-allowed",
                                    backgroundColor: hasFormChanges? "#ff810e" : "#000"
                                }}
                                disabled={!isEditing || !hasFormChanges || isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
                </div>
            </div>
            <PhoneVerificationModal
                isOpen={showPhoneVerification}
                onClose={() => setShowPhoneVerification(false)}
                user={localUser}
                onVerified={(updatedUser) => {
                    setShowPhoneVerification(false);
                    setPhoneVerified(true);
                    setFormData((prev) => ({ ...prev, phone: updatedUser?.phone_no || prev.phone }));
                    setUserInfoData((prev) => ({ ...prev, phone: updatedUser?.phone_no || prev.phone }));
                }}
            />
        </>
    );
}
